import React, { useState, useEffect } from 'react';

// Comps
import { Image as ResponsiveImage } from '@arctheme-components/elements/Image/Image';
import Paragraph from '@arctheme-components/elements/Paragraph/Paragraph';
import { Headings } from '@arctheme-components/elements/Headings/Headings';
import { Button } from '@src/stories/elements/Button/Button';

// Hooks
import { useCurrentTextStyle } from '@src/hooks/useCurrentTextStyle';

// Types
import type { Order, OrdersLocales, Settings, OrderLineItem } from './types';

// Shopify helpers
import shopify from '../../../../../js/shopify';
import helpers from 'frontend/js/helpers';

interface AccountOrdersProps {
  orders: Order[];
  locales: OrdersLocales;
  settings: Settings;
  onClickOrder: (event: React.ClickEvent<HTMLLinkElement>, order: Order) => void;
}

const AccountOrders: React.FC<AccountOrdersProps> = ({ orders, locales, settings, onClickOrder }) => {
  const [enrichedOrders, setEnrichedOrders] = useState<Order[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { getCurrentTextStyle } = useCurrentTextStyle();

  // Use enriched orders if available, fallback to original orders
  const ordersToDisplay: Order[] =
    enrichedOrders.length > 0 ? enrichedOrders : orders && Array.isArray(orders) && orders.length > 0 ? orders : [];

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      const currentYear = new Date().getFullYear();
      let result = date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });

      if (result.includes(currentYear.toString())) {
        result = result.replace(`, ${currentYear.toString()}`, '');
      }

      return result;
    } catch {
      return dateString;
    }
  };

  const handleClickBuyAgain = (order: Order) => {
    const detail = order.lineItems.map((item: OrderLineItem) => ({
      variantId: item.variantId,
      quantity: item.quantity
    }));

    if ((window as any).triggerAction) {
      (window as any).triggerAction('runAddToCartAndCartOpen', detail);
    }
  };

  // Helper function to get responsive text styles using the hook
  const getResponsiveTextStyle = (styleKey: string) => {
    const styles = settings?.textStyles?.[styleKey];
    if (!styles) return '';

    return getCurrentTextStyle(styles.default, styles.enableResponsive, styles.mobile, styles.tablet, styles.ultrawide);
  };

  return (
    <>
      <div className={'account-orders-header flex mb-4 w-full'}>
        <Headings
          type={'h2'}
          text={settings?.texts?.header || 'My Orders'}
          classes={`flex items-center ${getResponsiveTextStyle('header')}`}
        />
      </div>
      <div className='w-full grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3'>
        {isLoading && (
          <div className='text-center py-8'>
            <p className='text-sm text-gray-500'>Loading product images...</p>
          </div>
        )}

        {ordersToDisplay.length > 0 ? (
          ordersToDisplay.map((order: Order, index: number) => {
            const { orderNumber, createdAt, fulfillmentStatus, lineItems, total, billingAddress, paymentStatus } = order;
            const itemCount = lineItems.length;

            let displayItems: OrderLineItem[] = [];
            let remainingCount = 0;

            if (itemCount === 1) {
              displayItems = lineItems.slice(0, 1);
              remainingCount = 0;
            } else if (itemCount === 2) {
              displayItems = lineItems.slice(0, 2);
              remainingCount = 0;
            } else if (itemCount === 3) {
              displayItems = lineItems.slice(0, 3);
              remainingCount = 0;
            } else if (itemCount > 3) {
              displayItems = lineItems.slice(0, 3);
              remainingCount = itemCount - 3;
            }

            return (
              <div className="account-orders__order bg-white px-4 lg:px-5 py-4 lg:py-5 border border-[#E5E5E6] w-full grid grid-cols-2 gap-7">
                <div className={`flex flex-col gap-1`}>
                  <p className="b3--bold">Order No.</p>
                  <p className={`${getResponsiveTextStyle('orderNumber')} b4`}>{orderNumber}</p>
                </div>
                <div className={`flex flex-col gap-1`}>
                  <p className="b3--bold">Fulfillment Status</p>
                  <p className={`${getResponsiveTextStyle('statusCard')} b4`}>{helpers.capitalizeFirst(fulfillmentStatus)}</p>
                </div>
                <div className={`flex flex-col gap-1`}>
                  <p className="b3--bold">Date</p>
                  <p className={`${getResponsiveTextStyle('dateCard')} b4`}>{createdAt}</p>
                </div>
                <div className={`flex flex-col gap-1`}>
                  <p className="b3--bold">Amount</p>
                  <p className={`${getResponsiveTextStyle('total')} b4`}>{total}</p>
                </div>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    onClickOrder(e as React.ClickEvent<HTMLLinkElement>, order);
                  }}
                  className="button button-primary cursor-pointer self-end justify-self-start col-span-2 lg:col-span-2">
                  View Order
                </button>
                </div>
            );
          })
        ) : (
          <div className='col-span-3 text-center py-12'>
            <Paragraph
              type='b2 text-center'
              text={settings?.texts?.noOrdersHeading || locales?.empty || 'No orders found'}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default AccountOrders;
